/**
 * L'introduction du test — entre la vérif technique et la simulation.
 *
 * Kayla, 06-10-2026 : « une sorte d'introduction où il faut qu'ils
 * apprennent, tu peux déjà mettre la vidéo que j'avais faite. Puis ensuite on
 * les met en conditions réelles. »
 *
 * Trois blocs, dans l'ordre où on les lit : ce qu'est le travail (les leçons,
 * `lecons.ts` — la même copie que l'onglet Aide du poste), la vidéo de Kayla,
 * puis ce qu'on va lui demander (les cinq étapes du bandeau). Un seul bouton
 * pour sortir.
 *
 * ⚠️ ON NE FORCE PAS LA VIDÉO. Le bouton « J'ai compris, je commence » est
 * actif tout de suite : un candidat qui la saute se le verra compter (temps
 * passé ici, jusqu'où il a regardé — `MesureDeLIntro`), pas bloquer. C'est une
 * information pour Kayla, pas une porte.
 *
 * 📱 GROS TEXTE, UNE COLONNE. Le test exige un ordinateur (le mur PC est
 * devant), mais l'écran est écrit pour se lire aussi sur un téléphone : 17 px,
 * une colonne de 720 px au plus, rien côte à côte.
 */
import { useRef, useState } from "react";

import { Badge, Button, Carte, Ligne, Lignes } from "@/shared/ui";
import { Play } from "@/shared/ui/icons";

import type { MesureDeLIntro } from "./notation";
import { LECONS, VIDEO_DE_KAYLA, morceaux } from "./lecons";
import { LIBELLES_ETAPES, NUMERO_ETAPE, TITRES_DU_RAIL, type EtapeJouee } from "./scenario";

/** Les cinq étapes annoncées — la consigne du bandeau, une par numéro. */
const ETAPES_ANNONCEES: ReadonlyArray<{ numero: number; titre: string; consigne: string }> =
  TITRES_DU_RAIL.map((titre, i) => {
    const numero = i + 1;
    // La PREMIÈRE étape portant ce numéro donne sa consigne (la 5 en a deux :
    // la transaction, puis Reprendre — on les dit ensemble).
    const etapes = (Object.keys(NUMERO_ETAPE) as EtapeJouee[]).filter(
      (e) => NUMERO_ETAPE[e] === numero,
    );
    return { numero, titre, consigne: etapes.map((e) => LIBELLES_ETAPES[e]).join(", puis ") };
  });

export function Paragraphe({ texte, className }: { texte: string; className?: string }) {
  return (
    <p className={className}>
      {morceaux(texte).map((m, i) =>
        m.gras ? (
          <b key={i} className="font-semibold text-fg">
            {m.texte}
          </b>
        ) : (
          <span key={i}>{m.texte}</span>
        ),
      )}
    </p>
  );
}

export function IntroDuTest({ onFini }: { onFini: (mesure: MesureDeLIntro) => void }) {
  const depart = useRef(Date.now());
  /** Jusqu'où il a regardé — le MAXIMUM atteint, pas la position courante :
   *  revenir en arrière pour revoir un passage ne doit pas lui retirer ce
   *  qu'il a déjà vu. */
  const [vuJusqua, setVuJusqua] = useState<number | null>(null);

  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="mx-auto flex w-full max-w-[720px] flex-col gap-6 pb-10 pt-2 text-[17px] leading-relaxed text-muted">
        <header className="flex flex-col gap-2">
          <h1 className="text-[28px] font-bold leading-tight text-fg">Avant de commencer</h1>
          <p>
            Lis ces quelques lignes, regarde la vidéo, puis on te met en situation : un vrai
            poste, de faux fans. <b className="font-semibold text-fg">Rien de ce que tu fais
            n'est envoyé à une vraie personne.</b>
          </p>
        </header>

        <section aria-label="Comment ça marche" className="flex flex-col gap-3">
          {LECONS.map((l) => (
            <Carte key={l.cle} remplissage="lg">
              <div className="flex gap-3.5">
                <span aria-hidden className="w-8 shrink-0 pt-0.5 text-center text-[24px]">
                  {l.emoji}
                </span>
                <div className="min-w-0 flex-1">
                  <h2 className="text-[19px] font-bold text-fg">{l.titre}</h2>
                  {l.texte.map((t, i) => (
                    <Paragraphe key={i} texte={t} className="mt-1.5" />
                  ))}
                </div>
              </div>
            </Carte>
          ))}
        </section>

        <section aria-label="La vidéo" className="flex flex-col gap-3">
          <h2 className="flex items-center gap-2 text-[19px] font-bold text-fg">
            <Play aria-hidden className="h-5 w-5 text-muted" />
            {VIDEO_DE_KAYLA.titre}
          </h2>
          {/* `preload="metadata"` : 169 Mo — on ne charge que la durée et la
              première image tant qu'il n'a pas appuyé sur lecture. */}
          <Carte remplissage="aucun" className="overflow-hidden">
            <video
              controls
              preload="metadata"
              playsInline
              src={VIDEO_DE_KAYLA.url}
              aria-label={VIDEO_DE_KAYLA.titre}
              className="block aspect-video w-full bg-sunken"
              onPlay={() => setVuJusqua((v) => v ?? 0)}
              onTimeUpdate={(e) => {
                const v = e.currentTarget;
                if (!v.duration || !Number.isFinite(v.duration)) return;
                const pct = Math.min(100, Math.round((v.currentTime / v.duration) * 100));
                setVuJusqua((avant) => Math.max(avant ?? 0, pct));
              }}
            />
          </Carte>
        </section>

        <section aria-label="Ce qu'on va te demander" className="flex flex-col gap-3">
          <h2 className="text-[19px] font-bold text-fg">Ce qu'on va te demander</h2>
          <Carte remplissage="aucun">
            <Lignes ariaLabel="Les cinq étapes">
              {ETAPES_ANNONCEES.map((e) => (
                <Ligne
                  key={e.numero}
                  avant={
                    <Badge ton="gris" size="md">
                      {e.numero}
                    </Badge>
                  }
                  etiquette={
                    /* une consigne est une phrase : la rangée passe à la ligne au lieu de s'ellipser */
                    <span className="block whitespace-normal text-[16px] font-normal leading-snug text-muted">
                      <b className="font-semibold text-fg">{e.titre}</b> — {e.consigne}
                    </span>
                  }
                />
              ))}
            </Lignes>
          </Carte>
          <p className="text-[15px]">
            Une consigne en haut de l'écran te dit toujours où tu en es. L'onglet{" "}
            <b className="font-semibold text-fg">Aide</b>, à droite, reprend tout ce qui est
            écrit ici.
          </p>
        </section>

        <Button
          variant="accent"
          size="xl"
          className="w-full"
          onClick={() => onFini({ ms: Date.now() - depart.current, videoPct: vuJusqua })}
        >
          J'ai compris, je commence
        </Button>
      </div>
    </div>
  );
}
